import { s } from "../style.js";
import { countByStatus, TASK_STATUSES } from "../data.js";
import StatusPill from "../components/StatusPill";
import { useState, useMemo, useRef, useEffect } from "react";
import KanbanBoard from "../components/KanbanBoard";
import type { Role, Page, Task, TaskStatus } from "../types";

const ownerOptions = (tasks: Task[]) => {
  const seen = new Map<string, string>();
  for (const t of tasks) {
    const key = t.owner || "Unassigned";
    if (!seen.has(key)) seen.set(key, t.owner || "");
  }
  return ["All", ...Array.from(seen.keys())];
};

const SkeletonRow = () => (
  <div
    style={s(
      "display:grid; grid-template-columns:70px 1fr 190px 130px; gap:16px; align-items:center; padding:14px 32px; border-bottom:1px solid var(--color-divider)"
    )}
  >
    <div style={s("width:40px; height:14px; background:var(--color-neutral-200); border-radius:0")} />
    <div style={s("width:180px; height:14px; background:var(--color-neutral-200); border-radius:0")} />
    <div style={s("width:80px; height:14px; background:var(--color-neutral-200); border-radius:0")} />
    <div style={s("width:60px; height:20px; background:var(--color-neutral-200); border-radius:0")} />
  </div>
);

export default function SprintPage({ tasks, tasksLoading, auth, onOpenTask, onCreate, onUpdate, recentlyUpdated }: { tasks: Task[]; tasksLoading: boolean; auth: Role; onOpenTask: (task: Task) => void; onCreate: () => void; onUpdate: (id: string, updates: { status?: TaskStatus; repos?: string[]; ownerId?: string | null }) => void; recentlyUpdated: Set<string> }) {
  const [view, setView] = useState("list");
  const [filterOwner, setFilterOwner] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const ownerRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ownerRef.current && !ownerRef.current.contains(e.target as Node)) setOwnerOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const owners = useMemo(() => ownerOptions(tasks), [tasks]);
  const filtered = tasks.filter((t) => {
    if (filterOwner !== "All") {
      const ownerKey = t.owner || "Unassigned";
      if (ownerKey !== filterOwner) return false;
    }
    if (filterStatus !== "All" && t.status !== filterStatus) return false;
    return true;
  });
  const inProgress = filtered.filter((t) => t.status === "In progress");
  const hasFilter = filterOwner !== "All" || filterStatus !== "All";

  const dropdownBtn = s(
    "all:unset; box-sizing:border-box; cursor:pointer; display:inline-flex; align-items:center; gap:8px; padding:8px 14px; border:2px solid var(--color-text); font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.08em; text-transform:uppercase; white-space:nowrap"
  );
  const dropdownMenu = s(
    "position:absolute; top:calc(100% + 2px); left:0; min-width:160px; background:var(--color-surface); border:2px solid var(--color-text); z-index:10; max-height:240px; overflow:auto"
  );
  const dropdownItem = (active: boolean) =>
    s(
      `all:unset; box-sizing:border-box; display:block; width:100%; padding:8px 14px; cursor:pointer; font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.06em; text-transform:uppercase${active ? "; background:var(--color-text); color:var(--color-bg)" : ""}`
    );

  return (
    <div style={s("flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden")}>

      {/* ── filter + toolbar row ── */}
      <div
        style={s(
          "flex:none; display:flex; align-items:center; gap:16px; padding:12px 32px; border-bottom:2px solid var(--color-divider)"
        )}
      >
        {/* owner dropdown */}
        <div ref={ownerRef} style={{ position: "relative" }}>
          <button type="button" onClick={() => { setOwnerOpen(!ownerOpen); setStatusOpen(false); }} style={dropdownBtn}>
            Owner
            {filterOwner !== "All" && (
              <span
                onClick={(e) => { e.stopPropagation(); setFilterOwner("All"); }}
                style={s("display:inline-flex; align-items:center; gap:4px; padding:2px 8px; background:var(--color-text); color:var(--color-bg); font-size:12px")}
              >
                {filterOwner} ×
              </span>
            )}
            {filterOwner === "All" && <span style={{ opacity: 0.5 }}>▾</span>}
          </button>
          {ownerOpen && (
            <div style={dropdownMenu}>
              {owners.map((o) => (
                <button key={o} type="button" onClick={() => { setFilterOwner(o); setOwnerOpen(false); }} style={dropdownItem(filterOwner === o)}>
                  {o}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* status dropdown */}
        <div ref={statusRef} style={{ position: "relative" }}>
          <button type="button" onClick={() => { setStatusOpen(!statusOpen); setOwnerOpen(false); }} style={dropdownBtn}>
            Status
            {filterStatus !== "All" && (
              <span
                onClick={(e) => { e.stopPropagation(); setFilterStatus("All"); }}
                style={s("display:inline-flex; align-items:center; gap:4px; padding:2px 8px; background:var(--color-text); color:var(--color-bg); font-size:12px")}
              >
                {filterStatus} ×
              </span>
            )}
            {filterStatus === "All" && <span style={{ opacity: 0.5 }}>▾</span>}
          </button>
          {statusOpen && (
            <div style={dropdownMenu}>
              {["All", ...TASK_STATUSES].map((st) => (
                <button key={st} type="button" onClick={() => { setFilterStatus(st); setStatusOpen(false); }} style={dropdownItem(filterStatus === st)}>
                  {st}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* count */}
        <span
          style={s(
            "font-size:13px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600); white-space:nowrap"
          )}
        >
          {hasFilter ? `${filtered.length} of ${tasks.length}` : `${tasks.length} tasks`}
        </span>

        {/* spacer */}
        <div style={{ flex: 1 }} />

        {/* view toggle */}
        <div style={s("flex:none; display:flex")}>
          {[
            ["list", "List"],
            ["board", "Board"],
          ].map(([key, label]) => {
            const active = view === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                style={{
                  ...s(
                    "all:unset; box-sizing:border-box; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:10px 16px; border:2px solid var(--color-text); font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.08em; text-transform:uppercase"
                  ),
                  marginLeft: key === "board" ? "-2px" : "0",
                  background: active ? "var(--color-text)" : "transparent",
                  color: active ? "var(--color-bg)" : "var(--color-text)",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* new ticket */}
        {auth === "admin" && (
          <button
            onClick={onCreate}
            style={s(
              "all:unset; cursor:pointer; flex:none; display:inline-flex; align-items:center; gap:8px; padding:10px 18px; background:var(--color-text); color:var(--color-bg); border:2px solid var(--color-text); font-family:var(--font-heading); font-weight:800; font-size:14px; letter-spacing:0.08em; text-transform:uppercase"
            )}
          >
            + New ticket
          </button>
        )}
      </div>

      {view === "list" ? (
        <div
          style={s(
            "flex:1; display:grid; grid-template-columns:1fr 420px; min-height:0; overflow:hidden"
          )}
        >
        <section
          style={s(
            "min-width:0; min-height:0; overflow:hidden; display:flex; flex-direction:column; border-right:2px solid var(--color-divider)"
          )}
        >
          <div
            style={s(
              "flex:none; display:grid; grid-template-columns:70px 1fr 190px 130px; gap:16px; padding:10px 32px; border-bottom:2px solid var(--color-divider); font-size:13px; letter-spacing:0.12em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800"
            )}
          >
            <span>ID</span>
            <span>Active task</span>
            <span>Owner</span>
            <span>Status</span>
          </div>
          <div style={s("flex:1; min-height:0; overflow:auto")}>
            {tasksLoading ? (
              Array.from({ length: 5 }, (_, i) => <SkeletonRow key={i} />)
            ) : filtered.map((t) => (
              <div
                key={t.id}
                onClick={() => onOpenTask(t)}
                style={s(
                  "display:grid; grid-template-columns:70px 1fr 190px 130px; gap:16px; align-items:center; padding:14px 32px; border-bottom:1px solid var(--color-divider); cursor:pointer"
                )}
              >
                <span
                  style={s(
                    "font-size:17px; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600); font-variant-numeric:tabular-nums"
                  )}
                >
                  {t.id}
                </span>
                <div style={s("min-width:0")}>
                  <div style={s("display:flex; align-items:center; gap:8px")}>
                    {recentlyUpdated.has(t.id) && (
                      <span
                        style={{
                          ...s("width:8px; height:8px; flex:none; background:var(--color-accent); border-radius:50%"),
                          animation: "alertPulse 1.8s steps(1,end) infinite",
                        }}
                      />
                    )}
                    <div
                      style={s(
                        "font-size:23px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; text-wrap:pretty"
                      )}
                    >
                      {t.title}
                    </div>
                  </div>
                  <div style={s("display:flex; flex-wrap:wrap; gap:6px; margin-top:5px")}>
                    {t.repos.slice(0, 2).map((repo, i) => (
                      <span
                        key={repo}
                        style={s(
                          "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)"
                        )}
                      >
                        {t.repoNames?.[i] || repo}
                      </span>
                    ))}
                    {t.repos.length > 2 && (
                      <span
                        style={s(
                          "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-300); color:var(--color-neutral-700)"
                        )}
                      >
                        +{t.repos.length - 2}
                      </span>
                    )}
                  </div>
                </div>
                <div style={s("display:flex; align-items:center; gap:10px; min-width:0")}>
                  <span
                    style={s(
                      "width:38px; height:38px; flex:none; background:var(--color-text); color:var(--color-bg); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:14px; letter-spacing:0.02em"
                    )}
                  >
                    {t.initials || "?"}
                  </span>
                  <span
                    style={s(
                      "font-size:17px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis"
                    )}
                  >
                    {t.owner || "Unassigned"}
                  </span>
                </div>
                <div style={s("display:flex; align-items:center; justify-content:space-between; gap:8px")}>
                  <StatusPill task={t} />
                  <span style={s("font-size:22px; color:var(--color-neutral-500); line-height:1")}>›</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <aside
          style={s(
            "min-width:0; min-height:0; background:var(--color-text); color:var(--color-bg); display:flex; flex-direction:column; overflow:auto"
          )}
        >
          <div
            style={s(
              "flex:none; display:flex; align-items:center; gap:12px; padding:16px 26px; border-bottom:2px solid rgba(243,242,242,0.45)"
            )}
          >
            <span
              style={s(
                "font-size:20px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
              )}
            >
              {inProgress.length} In progress
            </span>
          </div>
          {inProgress.map((t) => (
            <div
              key={t.id}
              onClick={() => onOpenTask(t)}
              style={s(
                "padding:22px 26px; border-bottom:2px solid rgba(243,242,242,0.45); cursor:pointer"
              )}
            >
              <div style={s("display:flex; align-items:center; gap:8px; margin-bottom:8px")}>
                <span
                  style={s(
                    "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; opacity:0.8"
                  )}
                >
                  ATL-{t.id}
                </span>
                {t.repos.slice(0, 2).map((repo, i) => (
                  <span
                    key={repo}
                    style={s(
                      "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-bg); color:var(--color-text)"
                    )}
                  >
                    {t.repoNames?.[i] || repo}
                  </span>
                ))}
                {t.repos.length > 2 && (
                  <span
                    style={s(
                      "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:rgba(243,242,242,0.25); color:var(--color-bg)"
                    )}
                  >
                    +{t.repos.length - 2}
                  </span>
                )}
              </div>
              <div style={s("display:flex; align-items:center; gap:8px")}>
                {recentlyUpdated.has(t.id) && (
                  <span
                    style={{
                      ...s("width:8px; height:8px; flex:none; background:var(--color-accent); border-radius:50%"),
                      animation: "alertPulse 1.8s steps(1,end) infinite",
                    }}
                  />
                )}
                <div
                  style={s(
                    "font-size:30px; line-height:1.06; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.02em; text-wrap:pretty"
                  )}
                >
                  {t.title}
                </div>
              </div>
              <div style={s("display:flex; align-items:center; gap:10px; margin-top:14px")}>
                <span
                  style={s(
                    "width:36px; height:36px; background:var(--color-bg); color:var(--color-text); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:14px"
                  )}
                >
                  {t.initials || "?"}
                </span>
                <span style={s("font-size:17px")}>{t.owner || "Unassigned"}</span>
              </div>
            </div>
          ))}
        </aside>
        </div>
      ) : (
        <KanbanBoard tasks={filtered} onOpenTask={onOpenTask} onUpdate={onUpdate} recentlyUpdated={recentlyUpdated} />
      )}
    </div>
  );
}
